import { dayjs } from "@lib";
import {
	Bar,
	BarChart,
	CartesianGrid,
	ResponsiveContainer,
	Tooltip,
	XAxis,
	YAxis,
} from "recharts";
import { cx } from "@/utils/cx";
import {
	REPORT_SUMMARY_CATEGORY_COLORS,
	REPORT_SUMMARY_CATEGORY_NAMES,
	REPORT_SUMMARY_CATEGORY_TEXT_COLORS,
} from "../constants";
import type { ExpenseTypeCode, MonthlyEvolution } from "../types";
import { formatCurrency } from "../utils";

type EvolutionTooltipProps = {
	active?: boolean;
	payload?: Array<{
		name: string;
		value: number;
		color: string;
		dataKey: string;
	}>;
	label?: string;
};

function EvolutionTooltip({ active, payload, label }: EvolutionTooltipProps) {
	if (!(active && payload?.length)) {
		return null;
	}
	return (
		<div className="rounded-lg border border-[#E9EAEB] bg-white p-3 shadow-lg">
			<p className="mb-2 font-medium text-[#0A0D12]">{label}</p>
			{payload.map((entry) => (
				<div className="flex items-center gap-2 text-sm" key={entry.dataKey}>
					<div
						className="size-3 shrink-0 rounded-full"
						style={{ backgroundColor: entry.color }}
					/>
					<span className="text-[#535862]">{entry.name}:</span>
					<span className="font-medium text-[#0A0D12]">
						{formatCurrency(entry.value ?? 0)}
					</span>
				</div>
			))}
		</div>
	);
}

type ExpensesEvolutionChartProps = {
	data: MonthlyEvolution[];
};

export function ExpensesEvolutionChart({ data }: ExpensesEvolutionChartProps) {
	const chartData = data?.map((item: MonthlyEvolution) => {
		const totalsByType = item.totalsByType.reduce<Record<string, number>>(
			(acc, type) => {
				const label = REPORT_SUMMARY_CATEGORY_NAMES[type.type];
				acc[label] = type.total;
				return acc;
			},
			{}
		);

		return {
			monthYear: dayjs(`${item.year}-${item.month}`)
				.format("MMM/YY")
				.toUpperCase(),
			month: item.month,
			year: item.year,
			total: item.total,
			...totalsByType,
		};
	});

	const categoryNames = Object.keys(REPORT_SUMMARY_CATEGORY_NAMES) || [];

	return (
		<div className="rounded-xl border border-[#E9EAEB] bg-white p-5">
			<h3 className="mb-4 font-semibold text-[#0A0D12] text-lg">
				Evolução Mensal
			</h3>
			<div className="h-[240px] w-full">
				<ResponsiveContainer
					aspect={undefined}
					height={240}
					minHeight={0}
					minWidth={0}
					width="100%"
				>
					<BarChart data={chartData} margin={{ left: -30 }}>
						<CartesianGrid stroke="#E9EAEB" strokeDasharray="3 3" />
						<XAxis
							axisLine={{ stroke: "#E9EAEB" }}
							dataKey="monthYear"
							tick={{ fill: "#535862", fontSize: 12 }}
						/>
						<YAxis
							axisLine={{ stroke: "#E9EAEB", strokeWidth: 0 }}
							tick={{ fill: "#535862", fontSize: 11 }}
							tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`}
						/>
						<Tooltip content={<EvolutionTooltip />} />
						{categoryNames.map((key, index, array) => {
							const keyNumber = Number(key) as ExpenseTypeCode;

							const color = REPORT_SUMMARY_CATEGORY_COLORS[keyNumber];
							const name = REPORT_SUMMARY_CATEGORY_NAMES[keyNumber];

							return (
								<Bar
									dataKey={name}
									fill={color}
									key={key}
									minPointSize={0.1}
									name={name}
									radius={
										index === array.length - 1 ? [4, 4, 0, 0] : [0, 0, 0, 0]
									}
									stackId="stack"
								/>
							);
						})}
					</BarChart>
				</ResponsiveContainer>
			</div>
			<div className="mt-4 flex flex-wrap justify-start gap-4">
				{categoryNames.map((key) => {
					const keyNumber = Number(key) as ExpenseTypeCode;

					const color = REPORT_SUMMARY_CATEGORY_COLORS[keyNumber];
					const textColor = REPORT_SUMMARY_CATEGORY_TEXT_COLORS[keyNumber];
					const name = REPORT_SUMMARY_CATEGORY_NAMES[keyNumber];

					return (
						<div className="flex items-center gap-2" key={key}>
							<div
								className="size-2 shrink-0 rounded-full"
								style={{
									backgroundColor: color,
								}}
							/>
							<span className={cx("text-xs", textColor)}>{name}</span>
						</div>
					);
				})}
			</div>
		</div>
	);
}
